<template>
    <div class="pa-ui pa-page rma-page">
        <header class="pa-page-header">
            <div class="pa-page-header__row rma-header">
                <div>
                    <h1 class="pa-title">Documentos do mês</h1>
                    <p class="pa-subtitle">Envie aqui os documentos mensais que a Administração Judicial pede para acompanhar a recuperação judicial.</p>
                </div>
                <v-btn outlined to="/fiscalizacao-mensal/historico" class="rma-header__action">
                    <v-icon left small>mdi-history</v-icon>Histórico
                </v-btn>
            </div>
        </header>

        <v-progress-linear v-if="loading" indeterminate color="primary"></v-progress-linear>

        <div v-else-if="loadError" class="pa-card">
            <div class="pa-empty">
                <v-icon large>{{ loadError.blocked ? "mdi-lock-outline" : "mdi-alert-circle-outline" }}</v-icon>
                <div>{{ loadError.message }}</div>
                <v-btn v-if="!loadError.blocked" outlined small class="mt-4" @click="load">Tentar de novo</v-btn>
            </div>
        </div>

        <div v-else-if="!competences.length" class="pa-card">
            <div class="pa-empty">
                <v-icon large>mdi-calendar-month-outline</v-icon>
                <div class="rma-empty__title">Ainda não há documentos solicitados</div>
                <div>A primeira solicitação chega por e-mail no dia 1 do mês, com a lista de documentos e o prazo de envio.</div>
            </div>
        </div>

        <template v-else>
            <section class="pa-card rma-filters">
                <div class="rma-filters__field">
                    <div class="pa-label">Competência (mês de referência)</div>
                    <v-select :value="competenceId" :items="competences" item-text="label" item-value="id" outlined dense hide-details
                        :disabled="busy" :menu-props="{ offsetY: true }" @change="selectCompetence">
                        <template v-slot:item="{ item: option }">
                            <div class="rma-option">
                                <span class="rma-option__name">{{ option.label }}</span>
                                <span class="rma-option__meta">{{ competenceHint(option) }}</span>
                            </div>
                        </template>
                    </v-select>
                </div>
                <div class="rma-filters__field rma-filters__field--wide">
                    <div class="pa-label">Empresa</div>
                    <v-select v-model="companyId" :items="companyOptions" item-text="name" item-value="id" outlined dense hide-details
                        :disabled="busy || !companyOptions.length" :menu-props="{ offsetY: true }" no-data-text="Nenhuma empresa"
                        @change="rememberCompany">
                        <template v-slot:item="{ item: option }">
                            <div class="rma-option">
                                <span class="rma-option__name">
                                    {{ option.name }}
                                    <span v-if="option.registration" class="rma-option__sub">{{ option.registration }}</span>
                                </span>
                                <span class="rma-option__meta">{{ option.counts.missing ? `faltam ${option.counts.missing}` : "tudo enviado" }}</span>
                            </div>
                        </template>
                    </v-select>
                </div>
                <div v-if="busy" class="rma-filters__busy">
                    <v-icon small>mdi-progress-upload</v-icon>Aguarde o fim dos envios para trocar o mês ou a empresa.
                </div>
            </section>

            <v-progress-linear v-if="loadingChecklist" indeterminate color="primary" class="mb-4"></v-progress-linear>

            <template v-if="competence && !loadingChecklist">
                <section class="pa-card rma-summary">
                    <div class="rma-summary__grid">
                        <div class="rma-stat">
                            <div class="pa-label">Prazo de envio</div>
                            <div class="rma-stat__value">{{ competence.due_date_label || formatDate(competence.due_date) }}</div>
                            <pa-badge v-if="overdueOpen" variant="danger" text="Prazo encerrado" />
                            <pa-badge v-else :variant="competenceTone" :text="competenceLabel" />
                        </div>
                        <div class="rma-stat">
                            <div class="pa-label">{{ companies.length > 1 ? "Esta empresa" : "Documentos" }}</div>
                            <div class="rma-stat__value">{{ companyCounts.delivered }} de {{ companyCounts.total }} <span class="rma-stat__unit">enviados</span></div>
                            <div class="pa-meter"><div class="pa-meter__fill" :style="{ width: `${percent(companyCounts)}%` }"></div></div>
                        </div>
                        <div v-if="companies.length > 1" class="rma-stat">
                            <div class="pa-label">Todas as empresas</div>
                            <div class="rma-stat__value">{{ totalCounts.delivered }} de {{ totalCounts.total }} <span class="rma-stat__unit">enviados</span></div>
                            <div class="pa-meter"><div class="pa-meter__fill pa-meter__fill--value" :style="{ width: `${percent(totalCounts)}%` }"></div></div>
                        </div>
                    </div>

                    <div class="rma-banners">
                        <div v-if="notice" class="rma-banner" :class="`rma-banner--${notice.tone}`">
                            <v-icon small>{{ notice.icon }}</v-icon>
                            <div>{{ notice.text }}</div>
                        </div>
                        <div v-if="returnedCount" class="rma-banner rma-banner--danger">
                            <v-icon small>mdi-alert-circle-outline</v-icon>
                            <div>{{ returnedText }}</div>
                        </div>
                    </div>
                </section>

                <rma-clarifications :clarifications="clarifications" :competence-label="competence.label" @answered="onClarificationAnswered" />

                <div v-if="companyItems.length" class="rma-list-bar">
                    <pa-pills v-model="filter" :items="filterItems" />
                </div>

                <div class="rma-items">
                    <rma-item-card v-for="item in companyItems" v-show="matchesFilter(item)" :key="item.id" :item="item"
                        :clarification="openClarificationOf(item)"
                        @updated="onUpdated" @busy="(value) => setBusy(item.id, value)" />
                </div>

                <div v-if="!visibleCount" class="pa-card">
                    <div class="pa-empty">
                        <v-icon large>mdi-file-check-outline</v-icon>
                        <div>{{ emptyFilterText }}</div>
                    </div>
                </div>
            </template>
        </template>
    </div>
</template>

<script>
import axios from "axios";
import { eventHub } from "@/main";
import PaBadge from "@/components/plan-adhesion/PaBadge.vue";
import PaPills from "@/components/plan-adhesion/PaPills.vue";
import RmaItemCard from "@/components/rma/RmaItemCard.vue";
import RmaClarifications from "@/components/rma/RmaClarifications.vue";
import { competenceStatus, countItems, errorMessage, formatDate, formatDayMonth, notifyError, percent } from "@/components/rma/rma.util";
import "@/components/plan-adhesion/pa-ui.scss";

const COMPANY_STORAGE_KEY = "rma.selectedCompany";

// Fiscalizacao mensal no Painel da Devedora: checklist da competencia por
// empresa, com envio de arquivos em cada documento. Aceita ?competencia=<id>.
export default {
    name: "RmaDocuments",

    components: { PaBadge, PaPills, RmaItemCard, RmaClarifications },

    data: () => ({
        loading: true,
        loadError: null,
        competences: [],
        competenceId: null,
        loadingChecklist: false,
        competence: null,
        companies: [],
        items: [],
        clarifications: [],
        companyId: null,
        filter: "all",
        busyItems: [],
    }),

    computed: {
        busy() {
            return this.busyItems.length > 0;
        },

        companyOptions() {
            return this.companies.map((company) => ({ ...company, counts: countItems(this.itemsOf(company.id)) }));
        },

        companyItems() {
            return this.itemsOf(this.companyId);
        },

        companyCounts() {
            return countItems(this.companyItems);
        },

        totalCounts() {
            const counts = this.competence && this.competence.counts;
            return counts ? { total: counts.total, delivered: counts.delivered, missing: counts.missing } : countItems(this.items);
        },

        isComplete() {
            return !!this.competence && this.competence.status === "complete";
        },

        overdueOpen() {
            return !!this.competence && !!this.competence.overdue && !this.isComplete;
        },

        competenceLabel() {
            return competenceStatus(this.competence && this.competence.status).label;
        },

        competenceTone() {
            return competenceStatus(this.competence && this.competence.status).tone;
        },

        notice() {
            const c = this.competence;
            if (!c) return null;
            if (this.isComplete) {
                return {
                    tone: "success",
                    icon: "mdi-check-circle-outline",
                    text: `Todos os documentos de ${c.label} foram enviados. A Administração Judicial confere os arquivos e avisa por e-mail se algum precisar de ajuste.`,
                };
            }
            if (c.overdue) {
                return {
                    tone: "warning",
                    icon: "mdi-clock-alert-outline",
                    text: `O prazo terminou em ${formatDayMonth(c.due_date)}. Ainda é possível enviar; os envios ficam marcados como fora do prazo.`,
                };
            }
            return {
                tone: "info",
                icon: "mdi-information-outline",
                text: `Envie os documentos de ${c.label} até ${c.due_date_label || formatDate(c.due_date)}. Você pode enviar aos poucos: o que já foi enviado fica salvo.`,
            };
        },

        returnedCount() {
            return this.companyItems.filter((item) => item.status === "returned").length;
        },

        returnedText() {
            return this.returnedCount === 1
                ? "1 documento foi devolvido para ajuste. Veja o motivo no documento e envie o arquivo corrigido."
                : `${this.returnedCount} documentos foram devolvidos para ajuste. Veja o motivo em cada um e envie os arquivos corrigidos.`;
        },

        filterItems() {
            const counts = this.companyCounts;
            return [
                { value: "all", text: "Todos", count: counts.total },
                { value: "missing", text: "Faltando", tone: "warning", count: counts.missing },
                { value: "delivered", text: "Enviados", tone: "success", count: counts.delivered },
            ];
        },

        visibleCount() {
            return this.companyItems.filter((item) => this.matchesFilter(item)).length;
        },

        emptyFilterText() {
            if (!this.companyItems.length) return "Nenhum documento foi pedido para esta empresa neste mês.";
            if (this.filter === "missing") return "Nenhum documento faltando para esta empresa. Obrigado!";
            return "Nenhum documento enviado ainda para esta empresa.";
        },
    },

    watch: {
        "$route.query.competencia"(value) {
            const id = Number(value);
            if (id && id !== this.competenceId && this.competences.some((row) => row.id === id)) this.selectCompetence(id);
        },
    },

    created() {
        this.load();
        eventHub.$on("client-context-changed", this.reset);
    },

    mounted() {
        window.addEventListener("dragover", this.blockStrayDrop);
        window.addEventListener("drop", this.blockStrayDrop);
        window.addEventListener("beforeunload", this.warnBeforeUnload);
    },

    beforeDestroy() {
        eventHub.$off("client-context-changed", this.reset);
        window.removeEventListener("dragover", this.blockStrayDrop);
        window.removeEventListener("drop", this.blockStrayDrop);
        window.removeEventListener("beforeunload", this.warnBeforeUnload);
    },

    async beforeRouteLeave(to, from, next) {
        if (!this.busy) return next();
        const leave = await this.$dialog.confirm({
            title: "Envio em andamento",
            text: "Ainda há arquivos sendo enviados. Se sair agora, os envios em andamento são cancelados e esses arquivos precisam ser enviados de novo.",
            actions: { false: "Aguardar", true: { text: "Sair e cancelar envios", color: "error" } },
        });
        next(!!leave);
    },

    methods: {
        formatDate,
        percent,

        itemsOf(companyId) {
            return this.items.filter((item) => Number(item.company_id) === Number(companyId));
        },

        competenceHint(row) {
            if (row.status === "complete") return "completo";
            const missing = row.counts ? row.counts.missing : 0;
            if (!missing) return "";
            return missing === 1 ? "falta 1" : `faltam ${missing}`;
        },

        matchesFilter(item) {
            if (this.filter === "missing") return !!item.missing;
            if (this.filter === "delivered") return !item.missing;
            return true;
        },

        reset() {
            this.competences = [];
            this.competenceId = null;
            this.competence = null;
            this.companies = [];
            this.items = [];
            this.companyId = null;
            this.busyItems = [];
            this.load();
        },

        async load() {
            this.loading = true;
            this.loadError = null;
            try {
                const result = await axios.get("/api/client/rma/competences");
                this.competences = result.data.content || [];
            } catch (error) {
                const status = error && error.response && error.response.status;
                this.competences = [];
                this.loadError = status === 403
                    ? { blocked: true, message: errorMessage(error, "A fiscalização mensal não está ativa para este caso.") }
                    : { blocked: false, message: errorMessage(error, "Não foi possível carregar a fiscalização mensal.") };
            } finally {
                this.loading = false;
            }
            if (this.competences.length) await this.selectCompetence(this.initialCompetenceId());
        },

        initialCompetenceId() {
            const fromQuery = Number(this.$route.query.competencia);
            if (fromQuery && this.competences.some((row) => row.id === fromQuery)) return fromQuery;
            const withMissing = this.competences.find((row) => row.counts && row.counts.missing > 0);
            return (withMissing || this.competences[0]).id;
        },

        async selectCompetence(id) {
            if (!id) return;
            this.competenceId = id;
            this.syncQuery(id);
            await this.loadChecklist();
        },

        syncQuery(id) {
            if (String(this.$route.query.competencia || "") === String(id)) return;
            this.$router.replace({ query: { ...this.$route.query, competencia: String(id) } }).catch(() => {});
        },

        async loadChecklist() {
            const id = this.competenceId;
            this.loadingChecklist = true;
            try {
                const result = await axios.get(`/api/client/rma/competences/${id}`);
                if (id !== this.competenceId) return;
                const content = result.data.content || {};
                this.competence = content.competence || null;
                this.companies = content.companies || [];
                this.items = content.items || [];
                this.clarifications = content.clarifications || [];
                this.replaceCompetence(this.competence);
                this.pickCompany();
            } catch (error) {
                if (id !== this.competenceId) return;
                this.competence = null;
                this.companies = [];
                this.items = [];
                this.clarifications = [];
                notifyError(this, error, "Não foi possível carregar os documentos deste mês.");
            } finally {
                if (id === this.competenceId) this.loadingChecklist = false;
            }
        },

        pickCompany() {
            const ids = this.companies.map((company) => Number(company.id));
            if (ids.includes(Number(this.companyId))) return;
            const stored = Number(this.storedCompany());
            if (stored && ids.includes(stored)) {
                this.companyId = stored;
                return;
            }
            const withMissing = this.companies.find((company) => countItems(this.itemsOf(company.id)).missing > 0);
            this.companyId = withMissing ? withMissing.id : (this.companies[0] ? this.companies[0].id : null);
        },

        storageKey() {
            return `${COMPANY_STORAGE_KEY}.${localStorage.getItem("selectedClientId") || ""}`;
        },

        storedCompany() {
            try {
                return localStorage.getItem(this.storageKey());
            } catch (error) {
                return null;
            }
        },

        rememberCompany(id) {
            try {
                localStorage.setItem(this.storageKey(), String(id));
            } catch (error) {
                // Preferencia de tela: sem armazenamento, so nao lembra a escolha.
            }
        },

        openClarificationOf(item) {
            return this.clarifications.find((row) => Number(row.item_id) === Number(item.id) && row.status === "open") || null;
        },

        onClarificationAnswered(updated) {
            const index = this.clarifications.findIndex((row) => row.id === updated.id);
            if (index >= 0) this.clarifications.splice(index, 1, updated);
        },

        replaceCompetence(competence) {
            if (!competence) return;
            const index = this.competences.findIndex((row) => row.id === competence.id);
            if (index >= 0) this.competences.splice(index, 1, competence);
        },

        // Resposta das acoes do item: { item, competence }. Atualiza so a linha e as contagens.
        onUpdated(content) {
            const item = content && content.item;
            if (!item) return;
            const index = this.items.findIndex((row) => row.id === item.id);
            if (index >= 0) this.items.splice(index, 1, item);
            if (content.competence && this.competence && content.competence.id === this.competence.id) {
                this.competence = content.competence;
                this.replaceCompetence(content.competence);
            }
        },

        setBusy(id, value) {
            const others = this.busyItems.filter((row) => row !== id);
            this.busyItems = value ? [...others, id] : others;
        },

        // Arquivo solto fora da area de envio nao deve abrir no navegador (e tirar a pessoa da tela).
        blockStrayDrop(event) {
            if (event.defaultPrevented) return;
            event.preventDefault();
            if (event.dataTransfer) event.dataTransfer.dropEffect = "none";
        },

        warnBeforeUnload(event) {
            if (!this.busy) return;
            event.preventDefault();
            event.returnValue = "";
        },
    },
};
</script>

<style lang="scss" scoped>
.rma-header {
    flex-wrap: wrap;
}

.rma-header__action {
    flex-shrink: 0;
}

.rma-empty__title {
    font-size: 15px;
    font-weight: 600;
    color: #0f172a;
    margin-bottom: 4px;
}

.rma-filters {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    gap: 12px 16px;
    padding: 16px 20px;
    margin-bottom: 16px;
}

.rma-filters__field {
    flex: 1 1 220px;
    min-width: 0;
    max-width: 320px;
}

.rma-filters__field--wide {
    flex: 2 1 280px;
    max-width: 520px;
}

.rma-filters__busy {
    flex-basis: 100%;
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12.5px;
    color: #64748b;
}

.rma-option {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    width: 100%;
    padding: 6px 0;
    font-size: 14px;
}

.rma-option__name {
    min-width: 0;
    color: #0f172a;
}

.rma-option__sub {
    display: block;
    font-size: 12px;
    color: #64748b;
}

.rma-option__meta {
    flex-shrink: 0;
    font-size: 12px;
    color: #64748b;
}

.rma-summary {
    padding: 18px 20px;
    margin-bottom: 16px;
}

.rma-summary__grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 16px 24px;
}

.rma-stat {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    min-width: 0;
}

.rma-stat__value {
    font-size: 20px;
    font-weight: 600;
    line-height: 1.2;
    color: #0f172a;
}

.rma-stat__unit {
    font-size: 13px;
    font-weight: 400;
    color: #64748b;
}

.rma-stat .pa-meter {
    width: 100%;
    max-width: 220px;
}

.rma-banners {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 16px;
}

.rma-banner {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 10px 12px;
    border-radius: 8px;
    font-size: 13.5px;
    line-height: 1.5;

    .v-icon {
        margin-top: 2px;
        color: inherit !important;
    }
}

.rma-banner--info {
    background: #f3f5fb;
    border: 1px solid #dfe3f1;
    color: #27306b;
}

.rma-banner--warning {
    background: #fffbeb;
    border: 1px solid #fde68a;
    color: #92400e;
}

.rma-banner--success {
    background: #f0fdf4;
    border: 1px solid #bbf7d0;
    color: #166534;
}

.rma-banner--danger {
    background: #fef2f2;
    border: 1px solid #fecaca;
    color: #991b1b;
}

.rma-list-bar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px;
    margin-bottom: 12px;
}

.rma-items {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

@media (max-width: 700px) {
    .rma-page {
        padding: 20px 16px 40px;
    }

    .rma-page .pa-title {
        font-size: 22px;
        line-height: 30px;
    }

    .rma-filters {
        padding: 14px 16px;
    }

    .rma-filters__field,
    .rma-filters__field--wide {
        flex-basis: 100%;
        max-width: none;
    }

    .rma-summary {
        padding: 16px;
    }
}
</style>
